// ============================================================================
// DeleteModal — the delete confirm (Design Constitution §4 Delete, C03, O07,
// Q28, Q38)
// ============================================================================
// Rendered via the ConfirmContext-owned `deleteConfirm` state, and directly by
// a few views. Cancel and Delete (danger fill) right-aligned; Esc and the
// backdrop cancel (O07), except that the backdrop never throws away a typed
// word (O01).
// typedWord: the word to type before Delete arms. It defaults to 'delete' so
// every existing caller keeps its typed confirm. Q28 keeps typing only for
// whole records with children that cannot be undone (pig batch, breeding pig,
// weigh-in session, user); a caller outside that list passes typedWord={null}
// when its area wave moves it.
// The confirms layer stays at zIndex 11000 until every legacy pop-up moves
// onto the §3 ladder (see ConfirmModal).
// ============================================================================
import React from 'react';
import {buttonDestructive, buttonDisabled, buttonSecondary} from '../lib/buttonStyles.js';
import {controlStyle, fieldLabelStyle} from '../lib/formStyles.js';
import {
  MODAL_WIDTHS,
  dialogBodyStyle,
  dialogFooterStyle,
  dialogHeaderStyle,
  dialogMessageStyle,
  dialogPanelStyle,
  dialogScrimStyle,
  dialogTitleStyle,
} from '../lib/dialogStyles.js';
import {useModalFocusTrap} from './useModalFocusTrap.js';

// Two call shapes exist and both supply the three required props:
// ConfirmContext builds this with React.createElement from its `deleteConfirm`
// state, and several views render it directly as JSX.
type DeleteModalProps = {
  msg?: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  typedWord?: string | null;
};

const DeleteModal = ({msg, onConfirm, onCancel, typedWord = 'delete'}: DeleteModalProps) => {
  const [typed, setTyped] = React.useState('');
  const ready = typedWord == null || typed.trim().toLowerCase() === typedWord;
  const {dialogRef, handleDialogKeyDown} = useModalFocusTrap<HTMLDivElement>({onCancel});
  const confirmAndClose = () => {
    onConfirm();
    onCancel();
  };

  return (
    <div
      data-delete-modal="1"
      data-overlay-dismiss={typed ? 'disabled' : 'enabled'}
      data-focus-trap="active"
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
      aria-describedby="delete-modal-message"
      tabIndex={-1}
      onKeyDown={handleDialogKeyDown}
      onMouseDown={(e) => {
        // A click on the scrim must not move focus out of the dialog, or Esc
        // (handled on the dialog) would stop working (O06).
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        if (!typed) onCancel();
      }}
      style={{...dialogScrimStyle, zIndex: 11000}}
    >
      <div style={dialogPanelStyle(MODAL_WIDTHS.sm)}>
        <div style={dialogHeaderStyle}>
          <h2 id="delete-modal-title" style={dialogTitleStyle}>
            Are you sure?
          </h2>
        </div>
        <div style={dialogBodyStyle}>
          <p id="delete-modal-message" style={{...dialogMessageStyle, marginBottom: typedWord == null ? 0 : 14}}>
            {msg}
          </p>
          {typedWord != null && (
            <>
              <label htmlFor="delete-modal-typed" style={fieldLabelStyle}>
                Type <strong>{typedWord}</strong> to confirm
              </label>
              <input
                id="delete-modal-typed"
                autoFocus
                data-modal-initial-focus="1"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && ready) {
                    confirmAndClose();
                  }
                }}
                aria-label="Type delete to confirm"
                placeholder={typedWord}
                autoComplete="off"
                style={controlStyle}
              />
            </>
          )}
        </div>
        <div style={{...dialogFooterStyle, justifyContent: 'flex-end'}}>
          <button
            type="button"
            className="wcf-clickable wcf-btn wcf-btn--secondary"
            data-modal-initial-focus={typedWord == null ? '1' : undefined}
            onClick={onCancel}
            style={buttonSecondary}
          >
            Cancel
          </button>
          <button
            type="button"
            className="wcf-clickable wcf-btn wcf-btn--destructive"
            disabled={!ready}
            onClick={confirmAndClose}
            style={{...buttonDestructive, ...(ready ? null : buttonDisabled)}}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteModal;
